import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Word } from '../../types/word.types';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { RotateCcw } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';
import WordPopover from '../../components/LearnWords/WordPopover';
import BackToPracticeButton from '../../components/ui/back-to-practice-button';

// Words will be passed via navigation state

function ExamplesPage() {
  const [searchParams] = useSearchParams();
  const selectedWords = useMemo(() => {
    const wordsParam = searchParams.get('w');
    if (!wordsParam) {
      return [];
    }
    return wordsParam.split(',').map(word => decodeURIComponent(word)).filter(word => word.trim());
  }, [searchParams]);
  const [isLoading, setIsLoading] = useState(false);

  const handleRegenerate = () => {
    setIsLoading(true);
    // Simulate backend delay
    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
  };

  // Function to highlight used words in the paragraph
  const highlightUsedWords = (text: string, usedWords: string[]) => {
    // Create a regex pattern for all used words (case insensitive, whole words only)
    const wordPattern = new RegExp(`\\b(${usedWords.join('|')})\\b`, 'gi');

    // Split text by the pattern and map to JSX elements
    const parts = text.split(wordPattern);

    return parts.map((part, index) => {
      const isUsedWord = usedWords.some(word =>
        word.toLowerCase() === part.toLowerCase()
      );

      if (isUsedWord) {
        // Create a mock word object for the popover
        const mockWord: Word = {
          id: `example-${part}`,
          word: part,
          definitions: [{ partOfSpeech: 'noun', meaning: 'Example word' }],
          addedAt: new Date(),
          rating: 3,
        };

        return (
          <WordPopover key={index} word={mockWord}>
            <span
              className="font-semibold text-primary border-b-2 border-dotted border-primary cursor-pointer"
              title="Click or hover to see definition"
            >
              {part}
            </span>
          </WordPopover>
        );
      }

      return <span key={index}>{part}</span>;
    });
  };

  // Generate example paragraphs using the words
  const exampleParagraphs = useMemo(() => {
    const words = selectedWords.slice(0, Math.min(8, selectedWords.length));

    return [
      {
        title: "Technology and Society",
        content: `In today's world, technology has become ${words.find(w => w.toLowerCase() === 'ubiquitous') || 'ubiquitous'} in our daily lives. The ${words.find(w => w.toLowerCase() === 'ephemeral') || 'ephemeral'} nature of social media trends means they come and go quickly. Through ${words.find(w => w.toLowerCase() === 'serendipity') || 'serendipity'}, we sometimes discover amazing opportunities we never expected. Leaders must be ${words.find(w => w.toLowerCase() === 'eloquent') || 'eloquent'} in their communication to inspire their teams. Organizations constantly work to ${words.find(w => w.toLowerCase() === 'ameliorate') || 'ameliorate'} challenging situations and improve outcomes.`,
        wordsUsed: selectedWords.slice(0, 5),
      },
      {
        title: "Personal Growth",
        content: `Building resilience character requires facing challenges head-on. The ephemeral moments of success remind us to stay humble. Through serendipitous encounters, we often find mentors who guide our path. Being eloquent in expressing our needs helps build stronger relationships. Every experience helps us ameliorate our understanding of ourselves.`,
        wordsUsed: words.slice(0, 5),
      },
    ];
  }, [selectedWords]);

  if (selectedWords.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 py-6 md:py-8">
        <BackToPracticeButton />
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">No words available for examples.</p>
            <p className="text-sm text-muted-foreground mt-2">
              Try selecting words in the playground first.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-6 md:py-8">
      <BackToPracticeButton />

      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Example Pagragraphs</h1>
            <p className="text-muted-foreground text-sm">
              See your vocabulary words in real contexts
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenerate}
            className="gap-2"
            disabled={isLoading}
          >
            <RotateCcw className={cn("h-4 w-4", isLoading && "animate-spin")} />
            {isLoading ? 'Regenerating...' : 'Regenerate'}
          </Button>
        </div>
      </div>

      {/* Example Paragraphs */}
      <div className="space-y-6">
        {isLoading ? (
          // Shimmer Loading State
          <>
            <Card>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                  <Skeleton className="h-7 w-48" />
                </div>
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                  <Skeleton className="h-7 w-48" />
                </div>
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          // Actual Paragraphs
          <>
            {exampleParagraphs.map((paragraph, index) => (
              <Card key={index}>
                <CardContent className="p-8">
                  {/* Header Section */}
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                    <h3 className="text-xl font-bold text-foreground">{paragraph.title}</h3>
                  </div>

                  {/* Content Section */}
                  <div className="text-lg leading-[3rem]">
                    <p>
                      {highlightUsedWords(paragraph.content, paragraph.wordsUsed)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default ExamplesPage;
